// Custom deal toys and financial tombstones: a demand-test page under the
// owner's traffic-first policy (.seo/brand.md, 2026-11-01). The studio makes
// custom awards and has quoted engraved commemorative pieces for a bank, but
// shows no deal toy client work, so images are AI-generated and captioned as
// such. Every price is "quoted per project"; the only figure is the trophy
// 3D-modeling fee, stated as "starts at". Plating is a metal layer over resin,
// never solid metal or real gold. Keyword targets: OpenSEO US 2026-11-02 (see
// .seo/content-ledger.md).
const { pricing, photos: trophyPhotos, photoUrl } = require('./custom-trophy')

const dealToyPricing = { modelingFrom: pricing.modelingFee }

const dealToys = {
  path: '/services/custom-trophy/deal-toys/',
  reviewedDate: '2026-11-02',
  reviewedDateLabel: 'November 2, 2026',
  title: 'Custom Deal Toys & Financial Tombstones | PrintAWorld',
  description:
    'Custom deal toys and financial tombstones for banks, funds and law firms, sculpted, 3D printed, plated and engraved in New York. Quoted per project.',
  answer:
    'PrintAWorld makes custom deal toys in New York: commemorative pieces for closed deals, IPOs, financings and fund launches. Instead of a clear Lucite block, we sculpt the deal itself, like a building, a product or a logo, then 3D print it, paint or metal-plate it and set it on a solid base with the deal names, size and date. Each deal toy is quoted per project.',
}

// AI-generated mockups (Codex image_gen, 2026-11-02), local webp in
// static/images/deal-toys/. They show concepts, not client work; captions say
// they are AI-generated. Owner: no crystal or clear acrylic, and every design
// must look sturdy enough to print and ship.
const images = {
  towerOnBase: {
    file: 'tower-on-base',
    alt: 'Custom deal toy of a gold-finish skyscraper model on a black base with an engraved brass plate',
    caption: 'A real estate deal: the tower itself, gold-plated',
  },
  sculptedTombstone: {
    file: 'sculpted-tombstone',
    alt: 'Matte black deal toy block with a gold-plated frame, a raised gold sculpted logo and an engraved metal plate',
    caption: 'The tombstone, sculpted instead of cast in Lucite',
  },
  puzzleMerger: {
    file: 'puzzle-merger',
    alt: 'Two interlocking sculpted pieces, one navy and one gold-finish, joined on a black base with an engraved plate',
    caption: 'A merger: two companies, one piece',
  },
  ipoRocket: {
    file: 'ipo-rocket',
    alt: 'Silver-plated rocket standing on a thick swirling gold-plated exhaust plume, on a black base with an engraved IPO plate',
    caption: 'An IPO, launched',
  },
  energyDiorama: {
    file: 'energy-diorama',
    alt: 'Miniature wind farm and solar field diorama set into a walnut base with an engraved brass plate',
    caption: 'An energy financing as a desk-size diorama',
  },
  dealTeamSet: {
    file: 'deal-team-set',
    alt: 'Row of identical gold-plated monogram deal toys on black cube bases, each with a small engraved plate',
    caption: 'One design, a copy for every deal team member',
  },
}

// Real studio work from the custom trophy page and the trophy gallery
// (/gallery/custom-trophy-and-awards/), picked by the owner's request on
// 2026-11-02 because they read like deal toys: replicas of a product or an
// asset, logos and metal finishes on engraved bases. None was made as a deal
// toy, so captions describe the piece, not a deal.
const realWork = [
  {
    ...trophyPhotos.formicArm,
    caption: 'A robot arm award for Formic, a robotics company',
    layout: 'feature',
  },
  {
    file: '1pHKUpyGDZxmv2oyLVYPcQ/3d2272e7820c4729c90175199a9601a4/_LAL3401-3.jpg',
    alt: 'Two vintage truck replicas in a bronze finish on black bases with engraved plates',
    caption: 'Vintage truck replicas on engraved bases',
    layout: 'wide',
  },
  {
    file: '4hHPyCSd9B2uzyDz1pLgSf/a3b78a8455cdacd115c40ffbccc01c93/LAL_8953.jpg',
    alt: 'Three yellow brake caliper replicas on a black plaque with an engraved metal plate',
    caption: 'Product replicas on an engraved plaque',
    layout: 'wide',
  },
  {
    file: '20jvjlbn0gCjmExj0DkGn0/9816c268e3577001725201cba4b7f339/LAL_0012-5.jpg',
    alt: '3D printed Manhattan skyline model on a wood base',
    caption: 'A Manhattan skyline model on a wood base',
    layout: 'wide',
  },
  {
    file: '2kGRUXMD5ZNxm0dX2OFuGO/51a984c81237476d77349c546d6cddf6/LAL_7278.jpg',
    alt: 'Gold-finish sculpted brain on a black base engraved with a name and title',
    caption: 'A gold-finish sculpture on an engraved base',
  },
  {
    file: '5zP3ZdZScbFnipl4DE1kSu/23d548bdd261c699428a4c8b878a63ff/BeamieAward-8.jpg',
    alt: 'Silver-finish rocket and astronaut figures on wood block bases',
    caption: 'A silver-finish rocket on a wood base',
  },
]

const audiences = [
  {
    title: 'Investment banks and advisers',
    body: 'M&A, IPOs, debt and equity financings. One deal toy for every member of the deal team and the client, all from one design.',
  },
  {
    title: 'Private equity and venture funds',
    body: 'Acquisitions, exits, fund closes and portfolio milestones, for the partners and the founders across the table.',
  },
  {
    title: 'Law firms',
    body: 'Closings your team and the client will remember, with the parties, the size and the closing date engraved.',
  },
  {
    title: 'Real estate',
    body: 'A building, a development or a portfolio sale, sculpted as a scale model of the property on an engraved base.',
  },
  {
    title: 'Companies marking a deal',
    body: 'A merger, a launch, a big contract or a funding round. Founders give them to investors, staff and partners.',
  },
]

const ideas = [
  {
    title: 'The asset itself, sculpted',
    body: 'A model of the building, the ship, the plant, the product or the app icon at desk size. It says what the deal was about without a word of text.',
  },
  {
    title: 'The logo in raised relief',
    body: 'The client’s or the target’s logo sculpted in 3D, in a gold, silver or painted finish, instead of printed flat inside a Lucite block.',
  },
  {
    title: 'The tombstone, reimagined',
    body: 'The classic block shape with the parties, the deal size, the role and the date, made as a solid sculpted piece with a plated frame and a raised logo.',
  },
  {
    title: 'A symbol of the deal',
    body: 'Two interlocking pieces for a merger, a rocket for an IPO, a bridge for a cross-border deal. A simple idea, sculpted well, says more than a line of text.',
  },
  {
    title: 'A desk-size diorama',
    body: 'A wind farm, a port, a campus or a data center in miniature for an infrastructure or energy financing, on a wood base with an engraved plate.',
  },
  {
    title: 'An inside joke from the deal',
    body: 'The code name, the late nights, the thing everyone on the deal team remembers. It makes the piece the one people keep on the desk.',
  },
  {
    title: 'A set for the whole deal team',
    body: 'The design work is done once, then each copy is printed and finished, with a name plate for every person if you want one.',
  },
]

const steps = [
  'Send the deal details: the parties, the deal size, your role, the date and any logos.',
  'Tell us the idea, or ask us for one: the asset, the logo, a shape, the code name.',
  'We 3D model the piece and lay out the engraving, and send you both to approve.',
  'We resin print the sculpture and sand it smooth.',
  'We paint it, or plate it in a real metal finish such as gold or silver.',
  'We engrave the deal plates, mount every copy on its base and pack them.',
]

const comparison = [
  {
    title: 'A stock Lucite tombstone',
    body: 'Specialist deal toy makers offer dozens of stock acrylic shapes with your deal text engraved or a logo embedded inside. It costs less per piece, the shapes are proven, and it suits a bank that orders many a year.',
  },
  {
    title: 'A sculpted custom deal toy',
    body: 'The piece is modeled from the deal itself: the building, the product, the logo or the joke. It costs more and takes longer, because the design is made for one deal, and it’s the one nobody else on the shelf has.',
  },
]

const faqs = [
  {
    question: 'What is a deal toy?',
    answer:
      'A deal toy, also called a financial tombstone or a Lucite, is a keepsake made when a deal closes: an M&A transaction, an IPO, a financing or a fund close. It names the parties, the deal size, the role of the firm and the date, and is given to the deal team and the client.',
  },
  {
    question: 'How much does a custom deal toy cost?',
    answer: `Custom deal toys are quoted per project. A sculpted piece starts with 3D modeling, and our 3D modeling starts at $${dealToyPricing.modelingFrom}, paid once for the design. Printing, the paint or plated finish, the engraved base and the quantity are then priced from the design. Ask for several quantities and we price each one.`,
  },
  {
    question: 'Why is it called a financial tombstone?',
    answer:
      'Tombstones were the plain, bordered newspaper ads that announced a deal and listed the banks that worked on it. The block shape and the engraved list of parties carried over to the acrylic pieces given after closing, and the name stuck.',
  },
  {
    question: 'Do you make Lucite tombstones?',
    answer:
      'Our deal toys are sculpted, painted or plated pieces on solid bases, not clear Lucite blocks. If you want the classic clear block with a logo embedded inside, a stock Lucite maker is the right choice. If you want the deal itself sculpted, like the building, the product or the logo, that’s what we make.',
  },
  {
    question: 'Can you make the deal toy look like the building or product?',
    answer:
      'Yes, that’s what we do best. Send photos, drawings or a 3D file of the building, the vehicle, the product or the site, and we sculpt it at desk size. You approve the 3D model before anything is printed.',
  },
  {
    question: 'Is the gold finish solid metal or real gold?',
    answer:
      'No. The sculptures are printed in resin. A gold or silver look is either paint or metal plating, a real metal layer over the printed piece. Plating looks and feels like metal, but the piece is not solid metal and it is not real gold.',
  },
  {
    question: 'How many deal toys can you make for one deal?',
    answer:
      'From one to a full set for the deal team and the client. The 3D model is paid once, then each copy is printed, finished and engraved. Tell us the count and any names, and we quote each quantity you ask about.',
  },
  {
    question: 'How long do custom deal toys take?',
    answer:
      'Timing is quoted with your price, because it depends on the sculpt, the finish and the count. Tell us when you need them in hand, such as a closing dinner, and we’ll tell you what fits. The 3D model needs your approval first, so start as soon as the deal closes.',
  },
  {
    question: 'Can you keep the deal confidential?',
    answer:
      'Yes. Tell us what can’t be shown and we keep it off our site and social media. If you need a confidentiality agreement before you send details, ask for one in your request.',
  },
  {
    question: 'Do you ship?',
    answer:
      'Yes, anywhere in the United States. Pickup from our New York studio is available by appointment.',
  },
]

module.exports = {
  dealToyPricing,
  photoUrl,
  realWork,
  dealToys,
  images,
  audiences,
  ideas,
  steps,
  comparison,
  faqs,
}
